import './qbooksRequestCard.scss';

import type { ExtractComponentProp } from '@approvalmax/types';
import { ScrollableArea } from '@approvalmax/ui';
import { errorHelpers } from '@approvalmax/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { QueryKeys, selectors } from 'modules/common';
import { domain, State } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { isGrnAvailable } from 'modules/utils';
import { reloadRequestList } from 'pages/requestList/actions/actions';
import { FeaturePromotion } from 'pages/requestList/components/FeaturePromotion/FeaturePromotion';
import { RequestListPayAndCapturePromotion } from 'pages/requestList/components/RequestListPayAndCapturePromotion/RequestListPayAndCapturePromotion';
import { memo, useCallback, useEffect } from 'react';
import bemFactory from 'react-bem-factory';
import { useUnmount } from 'react-use';
import { QBooksMatchingPopup, QBooksMatchingPopupOld } from 'shared/components';
import { useAddMatching } from 'shared/data/webApp/v1';
import { useUserBetaFeatures } from 'shared/hooks';
import { AddonPromotionType, AftPromotionType, pageVisiblePromotionsState } from 'shared/states';

import FieldEntry from '../../../components/FieldEntry';
import GoodsReceivedNotes from '../../../components/GoodsReceivedNotes/GoodsReceivedNotes';
import SectionContainer from '../../../components/SectionContainer';
import ActivitySection from '../../card/ActivitySection';
import AddCommentSection from '../../card/AddCommentSection/AddCommentSection';
import AttachmentsSection from '../../card/AttachmentsSection';
import Toolbar from '../../card/Toolbar/Toolbar';
import { WorkflowSection } from '../../card/WorkflowSection';
import { EmailToCounterpartySection } from '../EmailToCounterpartySection/EmailToCounterpartySection';
import QBooksAccountLineItemsSection from '../QBooksAccountLineItemsSection/QBooksAccountLineItemsSection';
import QBooksBillFields from '../QBooksBillFields/QBooksBillFields';
import { QBooksBudgetingSection } from '../QBooksBudgetingSection/QBooksBudgetingSection';
import QBooksExpenseFields from '../QBooksExpenseFields/QBooksExpenseFields';
import QbooksVendorFields from '../QbooksFields/QbooksVendorFields';
import QBooksJournalEntryCard from '../QBooksJournalEntryCard/QBooksJournalEntryCard';
import QBooksLineItemsSection from '../QBooksLineItemsSection/QBooksLineItemsSection';
import { QBooksMemoFields } from '../QBooksMemoFields/QBooksMemoFields';
import QBooksPendingStatusToolbarSection from '../QBooksPendingStatusToolbarSection';
import QBooksPurchaseOrderFields from '../QBooksPurchaseOrderFields/QBooksPurchaseOrderFields';
import { QBooksSalesInvoiceFields } from '../QBooksSalesInvoiceFields/QBooksSalesInvoiceFields';
import { QBooksSalesInvoiceShippingDetails } from '../QBooksSalesInvoiceShippingDetails/QBooksSalesInvoiceShippingDetails';
import { useClosePromotion, useGetNextPromotions, useQBooksMatchingRemoveData } from './QBooksRequestCard.hooks';
import { messages } from './QBooksRequestCard.messages';
import { QBooksRequestCardProps } from './QBooksRequestCard.types';

const bem = bemFactory.block('reql-qbooks-request-card');
const qa = bemFactory.qa('reql-qbooks-request-card');

export const QBooksRequestCard = memo<QBooksRequestCardProps>((props) => {
    const { className, request, readonly } = props;

    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const company = useSelector((state: State) => selectors.company.getCompanyById(state, request.companyId));
    const attachments = request.attachments;

    const workflows = useSelector((state: State) =>
        selectors.template.getIntegrationTemplatesByCompanyId(state, company.id)
    );

    const { isBelgradeUIQBooksMatchingPopup } = useUserBetaFeatures();

    const [pageVisiblePromotions, setPageVisiblePromotions] = useAtom(pageVisiblePromotionsState);
    const getNextPromotion = useGetNextPromotions();

    const closePromotion = useClosePromotion();

    useEffect(() => {
        const promotion = getNextPromotion(request, company, workflows);

        setPageVisiblePromotions(promotion ? [promotion] : null);
    }, [company, getNextPromotion, request, setPageVisiblePromotions, workflows]);

    useUnmount(() => {
        setPageVisiblePromotions(null);
    });

    const visiblePromotion = pageVisiblePromotions?.[0];

    const hasBudgetingBeta = company.betaFeatures.includes(domain.CompanyBetaFeature.QBooksBudgeting);

    const showBudgetingSection =
        hasBudgetingBeta &&
        !company.isReadonly &&
        !readonly &&
        [domain.RequestStatusV2.OnApproval, domain.RequestStatusV2.OnHold, domain.RequestStatusV2.OnReview].includes(
            request.statusV2
        );

    const renderShippingDetails = (details: domain.QBooksPurchaseOrderDetails) => {
        const hasFilledValue =
            (details.departmentTerminology && details.department) ||
            details.shipping.method ||
            details.shipping.address;

        if (!hasFilledValue) {
            return null;
        }

        return (
            <SectionContainer text={messages.shippingDetailsHeaderText}>
                <div className={bem('shipping-details-fields')}>
                    {details.departmentTerminology && details.department && (
                        <FieldEntry
                            qa={qa('department-field')}
                            title={details.departmentTerminology}
                            value={details.department}
                        />
                    )}

                    {details.shipping.method && (
                        <FieldEntry
                            qa={qa('shipping-method-field')}
                            title={messages.shipViaFieldTitle}
                            value={details.shipping.method}
                        />
                    )}

                    {details.shipping.address && (
                        <FieldEntry
                            qa={qa('shipping-address-field')}
                            className={bem('shipping-address-field')}
                            title={messages.shipAddressFieldTitle}
                            value={details.shipping.address}
                        />
                    )}
                </div>
            </SectionContainer>
        );
    };

    if (request.integrationType !== domain.IntegrationType.QBooks) {
        throw errorHelpers.formatError();
    }

    const manualMatchingEnabled = request.company.billMatchingSettings?.manualMatchingEnabled ?? false;
    const showManualMatching =
        manualMatchingEnabled &&
        selectors.company.getQBOMatchingIsAvailable(request.company) &&
        selectors.request.isQbooksRequestReadyForMatching(request);

    const isVisibleGRN =
        isGrnAvailable(company, domain.IntegrationType.QBooks) &&
        company.qboPurchaseOrderSettings?.isGrnEnabled &&
        request.integrationCode === domain.IntegrationCode.QBooksPo &&
        request.statusV2 === domain.RequestStatusV2.Approved;

    let content;
    let lockDateToolbar;

    const { isLoading: isRemoveMatchingLoading, onMatchingRemove } = useQBooksMatchingRemoveData(request.companyId);

    switch (request.integrationCode) {
        case domain.IntegrationCode.QBooksPo: {
            const details = request.details;

            lockDateToolbar = <QBooksPendingStatusToolbarSection request={request} />;

            content = (
                <>
                    <QBooksPurchaseOrderFields request={request} />

                    {isVisibleGRN && <GoodsReceivedNotes request={request} readonly={readonly} />}

                    {!isVisibleGRN && visiblePromotion === AftPromotionType.GoodsReceivedNote && (
                        <FeaturePromotion
                            className={bem('feature-promotion')}
                            company={company}
                            integrationType={domain.IntegrationType.QBooks}
                            promotionType={AftPromotionType.GoodsReceivedNote}
                            onClose={closePromotion}
                        />
                    )}

                    <QBooksAccountLineItemsSection
                        request={request}
                        readonly={readonly}
                        onRemoveMatching={onMatchingRemove}
                        isRemoveMatchingLoading={isRemoveMatchingLoading}
                    />

                    <QBooksLineItemsSection
                        request={request}
                        onRemoveMatching={onMatchingRemove}
                        isRemoveMatchingLoading={isRemoveMatchingLoading}
                    />

                    {showBudgetingSection && <QBooksBudgetingSection request={request} company={company} />}

                    <QBooksMemoFields details={details} />

                    <AttachmentsSection request={request} />

                    {renderShippingDetails(details)}

                    <EmailToCounterpartySection request={request} />

                    {!showManualMatching && visiblePromotion === AftPromotionType.BillToPoMatchingPO && (
                        <FeaturePromotion
                            className={bem('feature-promotion')}
                            company={company}
                            integrationType={domain.IntegrationType.QBooks}
                            promotionType={AftPromotionType.BillToPoMatchingPO}
                            onClose={closePromotion}
                        />
                    )}

                    <WorkflowSection request={request} readonly={readonly} />

                    <ActivitySection request={request} />
                </>
            );
            break;
        }

        case domain.IntegrationCode.QBooksInvoice: {
            const details = request.details;

            lockDateToolbar = <QBooksPendingStatusToolbarSection request={request} />;

            content = (
                <>
                    <QBooksSalesInvoiceFields request={request} />

                    <QBooksLineItemsSection
                        request={request}
                        readonly={readonly}
                        onRemoveMatching={onMatchingRemove}
                        isRemoveMatchingLoading={isRemoveMatchingLoading}
                    />

                    <AttachmentsSection request={request} />

                    <QBooksSalesInvoiceShippingDetails details={details} />

                    <QBooksMemoFields details={details} />

                    <EmailToCounterpartySection request={request} />

                    <WorkflowSection request={request} readonly={readonly} />

                    <ActivitySection request={request} />
                </>
            );
            break;
        }

        case domain.IntegrationCode.QBooksBill: {
            const details = request.details;

            lockDateToolbar = <QBooksPendingStatusToolbarSection request={request} />;

            content = (
                <>
                    <QBooksBillFields request={request} />

                    <QBooksAccountLineItemsSection
                        request={request}
                        readonly={readonly}
                        onRemoveMatching={onMatchingRemove}
                        isRemoveMatchingLoading={isRemoveMatchingLoading}
                    />

                    <QBooksLineItemsSection
                        request={request}
                        readonly={readonly}
                        onRemoveMatching={onMatchingRemove}
                        isRemoveMatchingLoading={isRemoveMatchingLoading}
                    />

                    {showBudgetingSection && <QBooksBudgetingSection request={request} company={company} />}

                    <QBooksMemoFields details={details} />

                    <AttachmentsSection request={request} />

                    {!showManualMatching && visiblePromotion === AftPromotionType.BillToPoMatchingBill && (
                        <FeaturePromotion
                            className={bem('feature-promotion')}
                            company={company}
                            integrationType={domain.IntegrationType.QBooks}
                            promotionType={AftPromotionType.BillToPoMatchingBill}
                            onClose={closePromotion}
                        />
                    )}

                    <WorkflowSection request={request} readonly={readonly} />

                    <ActivitySection request={request} />
                </>
            );
            break;
        }

        case domain.IntegrationCode.QBooksExpense: {
            const details = request.details;

            lockDateToolbar = <QBooksPendingStatusToolbarSection request={request} />;

            content = (
                <>
                    <QBooksExpenseFields request={request} />

                    <QBooksAccountLineItemsSection
                        request={request}
                        readonly={readonly}
                        onRemoveMatching={onMatchingRemove}
                        isRemoveMatchingLoading={isRemoveMatchingLoading}
                    />

                    <QBooksLineItemsSection
                        request={request}
                        readonly={readonly}
                        onRemoveMatching={onMatchingRemove}
                        isRemoveMatchingLoading={isRemoveMatchingLoading}
                    />

                    {showBudgetingSection && <QBooksBudgetingSection request={request} company={company} />}

                    <QBooksMemoFields details={details} />

                    <AttachmentsSection request={request} />

                    <WorkflowSection request={request} readonly={readonly} />

                    <ActivitySection request={request} />
                </>
            );
            break;
        }

        case domain.IntegrationCode.QBooksVendor: {
            const details = request.details;

            content = (
                <>
                    <QbooksVendorFields contactData={details} />

                    <AttachmentsSection request={request} />

                    <WorkflowSection request={request} readonly={readonly} />

                    <ActivitySection request={request} />
                </>
            );
            break;
        }

        case domain.IntegrationCode.QBooksJournalEntry: {
            const details = request.details;

            lockDateToolbar = <QBooksPendingStatusToolbarSection request={request} />;

            content = (
                <QBooksJournalEntryCard
                    request={request}
                    company={company}
                    currency={request.currency}
                    details={details}
                    attachments={attachments}
                />
            );

            break;
        }

        default:
            throw errorHelpers.notSupportedError();
    }

    const { mutateAsync, isLoading: matchLoading } = useAddMatching({
        onSuccess: () => {
            queryClient.invalidateQueries([QueryKeys.QBOOKS_MATCHING_DATA]);
            dispatch(reloadRequestList());
        },
    });

    const handleMatch = useCallback<ExtractComponentProp<typeof QBooksMatchingPopup, 'onMatch'>>(
        (body) => mutateAsync({ body }),
        [mutateAsync]
    );

    return (
        <>
            {showManualMatching &&
                (isBelgradeUIQBooksMatchingPopup ? (
                    <QBooksMatchingPopup
                        requestId={request.id}
                        vendorId={request.details.vendor?.id}
                        billDate={request.details.date}
                        onMatch={handleMatch}
                        isLoading={matchLoading}
                    />
                ) : (
                    <QBooksMatchingPopupOld
                        requestId={request.id}
                        vendorId={request.details.vendor?.id}
                        billDate={request.details.date}
                        onMatch={handleMatch}
                        isLoading={matchLoading}
                    />
                ))}

            <div className={bem.add(className)()}>
                {(visiblePromotion === AddonPromotionType.Pay || visiblePromotion === AddonPromotionType.Capture) && (
                    <RequestListPayAndCapturePromotion type={visiblePromotion} />
                )}

                <Toolbar request={request} className={bem('toolbar')} readonly={readonly} />

                {lockDateToolbar}

                <ScrollableArea className={bem('scrollable-area')} scrollResetKey={request.id}>
                    <div className={bem('scrollable-content')}>{content}</div>
                </ScrollableArea>

                {!request.flags.status.isDraft && !readonly && (
                    <div className={bem('add-comment-section')}>
                        <AddCommentSection request={request} />
                    </div>
                )}
            </div>
        </>
    );
});

QBooksRequestCard.displayName = 'QBooksRequestCard';
